import { z } from 'zod';

import { api, isApiError } from '@/shared/api';
import { newUlid } from '@/shared/offline';

/**
 * Indienen is de onomkeerbare stap en kan alleen online
 * (`POST /inspections/{uuid}/submit`, zie §Opname in het contract). Afronden
 * kan wel offline en gaat via de outbox (`inspectionMutations.complete`).
 */

const submittedSchema = z.object({
    data: z.object({
        uuid: z.string(),
        status: z.string(),
        completed_at: z.string().nullable().default(null),
        submitted_at: z.string().nullable().default(null),
    }),
});

export type SubmittedInspection = z.infer<typeof submittedSchema>['data'];

/**
 * `409 /problems/inspection-reopen-locked`: de keuring is geannuleerd, de opname
 * gaat niet meer open. Een afgeronde keuring gaat wél open, ook na verzending.
 */
export class InspectionReopenLockedError extends Error {
    constructor(readonly reason: string | null) {
        super('Deze keuring is geannuleerd; de opname gaat niet meer open.');
        this.name = 'InspectionReopenLockedError';
    }
}

export const inspectionApi = {
    /**
     * Zet een ingediende opname weer open, met een reden - ook als het rapport al
     * verstuurd is. Alleen online, voor de eigenaar en de inspecteur van de opname;
     * de tablet krijgt haar bij de volgende synchronisatie weer bewerkbaar binnen.
     */
    async reopen(inspectionUuid: string, reason: string): Promise<SubmittedInspection> {
        try {
            const body = await api.post(
                `/inspections/${inspectionUuid}/reopen`,
                { reason },
                { idempotencyKey: newUlid(), retryAttempts: 1, cacheKey: false },
            );

            return submittedSchema.parse(body).data;
        } catch (error) {
            if (
                isApiError(error) &&
                error.status === 409 &&
                error.problem.type.endsWith('/problems/inspection-reopen-locked')
            ) {
                throw new InspectionReopenLockedError(
                    typeof error.problem.reason === 'string' ? error.problem.reason : null,
                );
            }

            throw error;
        }
    },

    /**
     * Dient de opname in. De server weigert een onvolledige opname niet: wat open
     * staat komt niet in het rapport, en een foto die nog onderweg is komt erna
     * binnen - het concept wordt dan herbouwd.
     */
    async submit(inspectionUuid: string): Promise<SubmittedInspection> {
        const body = await api.post(
            `/inspections/${inspectionUuid}/submit`,
            {},
            { idempotencyKey: newUlid(), retryAttempts: 1, cacheKey: false },
        );

        return submittedSchema.parse(body).data;
    },
};
